import serverIp from "./serverConfig";
import jwt_decode from "jwt-decode";
import {
  trackBeginCheckout,
  trackEvent,
  trackLegacyAnalytics
} from "./ga4";

const url = `${serverIp}/api/analytics`;

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /(?:\+?\d[\d\s\-().]{8,}\d)/g;
const CARD_PATTERN = /\b(?:\d[ -]*?){13,19}\b/g;
const TOKEN_PATTERN = /\b(?:eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)\b/g;
const LONG_NUMBER_PATTERN = /\b\d{7,}\b/g;

const sanitizeText = (value, limit = 180) => (
  String(value || "")
    .replace(EMAIL_PATTERN, "[redacted_email]")
    .replace(PHONE_PATTERN, "[redacted_phone]")
    .replace(CARD_PATTERN, "[redacted_card]")
    .replace(TOKEN_PATTERN, "[redacted_token]")
    .replace(LONG_NUMBER_PATTERN, "[redacted_number]")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, limit)
);

const getSafePage = (page) => sanitizeText(page || "UNKNOWN", 60);
const getSafeAction = (page, action) => {
  const pageKey = getSafePage(page).toLowerCase();
  if (pageKey === "assistantusermsg" || pageKey === "assistantresponse") {
    return `${pageKey}_logged`;
  }
  return sanitizeText(action || "UNKNOWN_ACTION", 180);
};

const getOrCreateSessionId = () => {
  if (typeof window === "undefined") return "";
  try {
    const key = "cd_analytics_session_id";
    const existing = sessionStorage.getItem(key) || localStorage.getItem(key);
    if (existing) return existing;
    const next = `s_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;
    sessionStorage.setItem(key, next);
    localStorage.setItem(key, next);
    return next;
  } catch {
    return "";
  }
};

const getUtmSource = () => {
  if (typeof window === "undefined") return "";
  try {
    const params = new URLSearchParams(window.location.search || "");
    return String(
      params.get("utm_source") ||
      params.get("utm_medium") ||
      params.get("utm_campaign") ||
      ""
    ).slice(0, 30);
  } catch {
    return "";
  }
};

const getPlanHint = () => {
  if (typeof window === "undefined") return "anon";
  try {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
    if (!token) return "anon";
    const decoded = jwt_decode(token);
    const delta = String(decoded?.delta || "");
    const username = String(decoded?.username || "").toLowerCase();
    const initial = String(decoded?.initial || "").toUpperCase();
    if (delta === "11A5D7A0C") return "plusmax";
    if (delta === "497CC0000") return "plus";
    if (initial === "TEMPORARY" || username.startsWith("trial-")) return "trial";
    return "base";
  } catch {
    return "auth";
  }
};

const buildContextSuffix = () => {
  if (typeof window === "undefined") return "";
  try {
    const sid = getOrCreateSessionId();
    const path = String(window.location.pathname || "/").slice(0, 40);
    const source = getUtmSource();
    const ref = (() => {
      try {
        return String(new URL(document.referrer || "").hostname || "").replace("www.", "").slice(0, 25);
      } catch {
        return "";
      }
    })();
    const viewport = window.innerWidth < 700 ? "m" : (window.innerWidth < 1100 ? "t" : "d");
    const plan = getPlanHint();
    const parts = [
      sid ? `sid:${sid}` : "",
      path ? `p:${path}` : "",
      source ? `utm:${source}` : "",
      ref ? `ref:${ref}` : "",
      viewport ? `vw:${viewport}` : "",
      plan ? `pl:${plan}` : ""
    ].filter(Boolean);
    return parts.length ? ` | ctx(${parts.join(",")})` : "";
  } catch {
    return "";
  }
};

const mapLegacyToGaEvents = (page, action) => {
  const pageKey = getSafePage(page).toLowerCase();
  const actionKey = String(action || "").toLowerCase();

  if (pageKey.includes("login") && actionKey.includes("logged in")) {
    trackEvent("login", { method: "email" }, { skipDb: true });
  }

  if (pageKey.includes("signup") && actionKey.includes("signed up successfully")) {
    trackEvent("sign_up", { method: "email" }, { skipDb: true });
  }

  if (pageKey.includes("pricing_plan")) {
    const billing = actionKey.includes("annual") ? "annual" : "monthly";
    trackEvent("select_item", {
      plan_tier: "PlusMAX",
      billing_cycle: billing,
      source: "pricing_page"
    }, { skipDb: true });
  }

  if (actionKey.includes("routed to signup by plusmax")) {
    const billing = actionKey.includes("annual") ? "annual" : "monthly";
    trackBeginCheckout({
      plan: "PlusMAX",
      billing,
      source: "frontpage_pricing"
    }, { skipDb: true });
  }

  if (pageKey === "plusmaxpayform" && actionKey.includes("stripe payment attempted")) {
    trackEvent("checkout_payment_attempt", {
      plan_tier: "PlusMAX",
      payment_method: "stripe"
    }, { skipDb: true });
  }

  if (pageKey === "plusmaxpayform" && actionKey.includes("paypal payment attempted")) {
    trackEvent("checkout_payment_attempt", {
      plan_tier: "PlusMAX",
      payment_method: "paypal"
    }, { skipDb: true });
  }

  if (pageKey === "map" && actionKey.includes("log out")) {
    trackEvent("logout", {}, { skipDb: true });
  }
};

const sendAnalytics = (page, action, date, internalOptions = {}) =>{
  const safePage = getSafePage(page);
  const safeAction = getSafeAction(page, action);
  const safeActionWithContext = `${safeAction}${buildContextSuffix()}`.slice(0, 320);
  const safeDate = String(date || new Date()).slice(0, 80);
  const shouldSkipGa = Boolean(internalOptions?.skipGa);

  if (!shouldSkipGa) {
    try {
      trackLegacyAnalytics({ page: safePage, action: safeActionWithContext, date: safeDate }, { skipDb: true });
      mapLegacyToGaEvents(safePage, safeActionWithContext);
    } catch {}
  }

  if(window.location.hostname!=='localhost' && window.location.hostname!=='127.0.0.1'){
    try{
      fetch(url,{
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control':'no-cache',
          'Referrer':`${serverIp}/`,
          // Include the token in the Authorization header
        },
        redirect: "follow", // manual, *follow, error
        // referrerPolicy:"no-referrer-when-downgrade",
        body: JSON.stringify({
          page: safePage,
          action: safeActionWithContext,
          date: safeDate,
          device:navigator.userAgent
        }), // body data type must match "Content-Type"
      });
    } catch {}
  }
};

if (typeof window !== "undefined") {
  window.__cdSendAnalytics = sendAnalytics;
}

export default sendAnalytics;
